import { useState, useEffect } from 'react';
import { QrCode, Download, CheckCircle, Loader2, AlertCircle, Clock, Sparkles } from 'lucide-react';
import { generateQRPromptPay, startPaymentPolling, PaymentVerificationResponse } from '../services/qrPromptPayService';

interface QRPromptPayPaymentProps {
  amount: number;
  planName: string;
  onPaymentSuccess: (chargeId: string) => void;
  onPaymentError: (error: string) => void;
  onCancel?: () => void;
}

export default function QRPromptPayPayment({ 
  amount, 
  planName,
  onPaymentSuccess,
  onPaymentError,
  onCancel
}: QRPromptPayPaymentProps) {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [sourceId, setSourceId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'successful' | 'failed' | 'expired'>('pending');
  const [timeRemaining, setTimeRemaining] = useState(900); // 15 minutes in seconds
  const [error, setError] = useState('');

  useEffect(() => {
    generateQR();
  }, []);

  useEffect(() => {
    if (qrCodeUrl && paymentStatus === 'pending') {
      // Start countdown timer
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setPaymentStatus('expired');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [qrCodeUrl, paymentStatus]);

  useEffect(() => {
    if (!sourceId || paymentStatus !== 'pending') return;

    // Start auto-verification polling
    setVerifying(true);
    const stopPolling = startPaymentPolling(
      sourceId,
      handlePaymentStatusChange,
      60, // 60 attempts
      15000 // Check every 15 seconds
    );

    return () => {
      stopPolling();
      setVerifying(false);
    };
  }, [sourceId]);

  const generateQR = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('[QR Payment] Generating QR Code for amount:', amount);
      const result = await generateQRPromptPay(amount);
      
      if (result.qr_code_url && result.source_id) {
        setQrCodeUrl(result.qr_code_url);
        setSourceId(result.source_id);
        console.log('[QR Payment] QR Code generated successfully');
      } else {
        throw new Error('ไม่สามารถสร้าง QR Code ได้');
      }
    } catch (err: any) {
      console.error('[QR Payment] Error generating QR:', err);
      const errorMessage = err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
      setError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentStatusChange = (status: PaymentVerificationResponse) => {
    console.log('[QR Payment] Payment status update:', status);

    if (status.status === 'successful' && status.charge_id) {
      setPaymentStatus('successful');
      setVerifying(false);
      onPaymentSuccess(status.charge_id);
    } else if (status.status === 'failed') {
      setPaymentStatus('failed');
      setVerifying(false);
      onPaymentError('การชำระเงินล้มเหลว กรุณาลองใหม่อีกครั้ง');
    } else if (status.status === 'expired') {
      setPaymentStatus('expired');
      setVerifying(false);
      onPaymentError('QR Code หมดอายุ กรุณาสร้าง QR Code ใหม่');
    }
  };

  const handleDownloadQR = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `promptpay-qr-${planName.replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8 text-center">
        <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
        <h3 className="text-lg font-bold text-gray-900 mb-2">กำลังสร้าง QR Code...</h3>
        <p className="text-gray-600">กรุณารอสักครู่</p>
      </div>
    );
  }

  if (error && !qrCodeUrl) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 text-center">
        <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          type="button"
          onClick={generateQR}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all"
        >
          ลองใหม่อีกครั้ง
        </button>
      </div>
    );
  }

  if (paymentStatus === 'successful') {
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 text-center border-2 border-green-300">
        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
          <CheckCircle className="w-12 h-12 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-green-900 mb-2">ชำระเงินสำเร็จ!</h3>
        <p className="text-green-700 mb-4">ระบบได้รับการชำระเงินของคุณแล้ว</p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-semibold">
          <Sparkles className="w-4 h-4" />
          ยืนยันอัตโนมัติสำเร็จ
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* QR Code Display */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
        <div className="bg-white rounded-xl p-6 mb-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <QrCode className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">สแกน QR Code เพื่อชำระเงิน</h3>
          </div>
          
          <img 
            src={qrCodeUrl} 
            alt="PromptPay QR Code" 
            className="w-64 h-64 mx-auto mb-4 border-4 border-blue-200 rounded-lg"
          />
          
          <div className="bg-blue-50 rounded-lg p-4 inline-block mb-4">
            <p className="text-sm text-gray-600 mb-1">จำนวนเงิน</p>
            <p className="text-3xl font-bold text-blue-600">฿{amount.toLocaleString()}</p>
          </div>

          {/* Timer */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-2">
            <Clock className="w-4 h-4" />
            <span>QR Code หมดอายุใน: <span className="font-bold text-blue-600">{formatTime(timeRemaining)}</span></span>
          </div>

          <p className="text-xs text-gray-500">สแกน QR Code นี้ด้วยแอปธนาคารของคุณ</p>
        </div>

        {/* Verification Status */}
        {verifying && paymentStatus === 'pending' && (
          <div className="bg-white rounded-lg p-4 mb-4 border-2 border-blue-200">
            <div className="flex items-center justify-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900">กำลังตรวจสอบการชำระเงิน...</p>
                <p className="text-xs text-gray-600">ระบบจะยืนยันอัตโนมัติเมื่อชำระเงินสำเร็จ</p>
              </div>
            </div>
          </div>
        )}

        {/* Download Button */}
        <button
          type="button"
          onClick={handleDownloadQR}
          className="w-full px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
        >
          <Download className="w-5 h-5" />
          บันทึก QR Code
        </button>
      </div>

      {/* Instructions */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          วิธีการชำระเงิน
        </h4>
        <ol className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
            <span>เปิดแอปธนาคารหรือแอป Mobile Banking ของคุณ</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
            <span>เลือกเมนู "สแกน QR" หรือ "PromptPay"</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
            <span>สแกน QR Code ด้านบน</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
            <span>ตรวจสอบจำนวนเงินและยืนยันการชำระเงิน</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">✓</span>
            <span className="font-semibold text-green-700">ระบบจะยืนยันการชำระเงินอัตโนมัติทันที!</span>
          </li>
        </ol>
      </div>

      {/* Auto-verify Badge */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg p-3 text-center">
        <div className="flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-bold">ยืนยันอัตโนมัติ - ไม่ต้องรอ Admin อนุมัติ</span>
        </div>
      </div>
    </div>
  );
}
